import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SalaryAccrualFormDto, type SalaryAccrualFormInterface } from '@zaoblako/shared/modules/document/dtos/salary-accrual/salary-accrual-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { SalaryAccrualService } from '@zaoblako/web/core/services/finance/salary-accrual.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { ContragentSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-single-select/contragent-single-select.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { SalaryAccrualFindInterface } from '@zaoblako/shared/modules/document/dtos/salary-accrual/salary-accrual-find.dto';

import { isNil } from 'lodash';
import moment from 'moment-timezone';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-document-salary-accrual-form',
  templateUrl: './document-salary-accrual-form.component.html',
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    JoinPipe,
    ButtonComponent,
    DatepickerComponent,
    InputNumberModule,
    SpinnerComponent,
    ContragentSingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentSalaryAccrualFormComponent extends BaseFormComponent<SalaryAccrualFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public dto = SalaryAccrualFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public salaryAccrual: SalaryAccrualFormInterface = {
    contragent: null,
    period: moment().startOf('month').toDate(),
    amount: 0
  };

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly salaryAccrualService: SalaryAccrualService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    if (entityId) {
      this.formStoreService.setLoading(true, this.index);
      this.salaryAccrualService.findById<SalaryAccrualFindInterface>(entityId).subscribe({
        next: (item) => {
          this.salaryAccrual = {
            contragent: item.contragent,
            period: item.period,
            amount: item.amount
          };
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        },
        error: () => this.formStoreService.setLoading(false, this.index)
      });
    }
  }

  public onContragentChange(contragent: ContragentFindInterface | null): void {
    this.salaryAccrual.contragent = contragent;
    if (!contragent) {
      this.cdRef.markForCheck();
      return;
    }
    if (!isNil(contragent?.salaryAmount)) {
      this.salaryAccrual.amount = Number(contragent.salaryAmount);
    }
    this.cdRef.markForCheck();
  }

  public saveSalaryAccrual(): void {
    if (!this.checkValidate(this.salaryAccrual).valid) {
      return;
    }

    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    this.formStoreService.setLoading(true, this.index);

    if (entityId) {
      this.salaryAccrualService
        .update<SalaryAccrualFormInterface & { id: number }, SalaryAccrualFindInterface>({
          ...this.salaryAccrual,
          id: entityId
        })
        .subscribe({
          next: (item) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Начисление ЗП №${entityId} обновлено`);
            this.formStoreService.saveNext(item, this.index);
          },
          error: () => this.formStoreService.setLoading(false, this.index)
        });
      return;
    }

    this.salaryAccrualService.create<SalaryAccrualFindInterface>(this.salaryAccrual).subscribe({
      next: (item) => {
        this.formStoreService.setLoading(false, this.index);
        this.errorService.addSuccessMessage('Начисление ЗП создано');
        if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
          this.formStoreService.closeSidebar(this.index);
          this.router.navigate(['/document/salary-accrual/card/' + item.id]).catch(console.error);
        } else {
          this.formStoreService.saveNext(item, this.index);
        }
      },
      error: () => this.formStoreService.setLoading(false, this.index)
    });
  }

  public deleteSalaryAccrual(): void {
    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить начисление ЗП №${entityId}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.salaryAccrualService.deleteById(entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Начисление ЗП №${entityId} удалено`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => this.formStoreService.setLoading(false, this.index)
        });
      }
    });
  }
}
